<template>
  <v-container>
    <v-card>
      <v-card-title class="d-flex align-center">
        Customers Overview
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          prepend-icon="mdi-refresh"
          :loading="loading"
          @click="refreshData"
        >
          Refresh
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-table>
          <thead>
            <tr>
              <th>Client Name</th>
              <th>Contact</th>
              <th>Token Status</th>
              <th>Errors</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="customer in customers" :key="customer.id">
              <td>{{ customer.name }}</td>
              <td>
                <div>{{ customer.contactName }}</div>
                <div class="text-caption text-grey">{{ customer.contactEmail }}</div>
              </td>
              <td>
                <v-chip
                  :color="getTokenStatusColor(customer.tokenStatus)"
                  size="small"
                  variant="elevated"
                >
                  {{ customer.tokenStatus }}
                </v-chip>
              </td>
              <td>
                <v-chip
                  v-if="errorCounts[customer.id]"
                  color="error"
                  size="small"
                  variant="elevated"
                >
                  {{ errorCounts[customer.id] }} errors
                </v-chip>
                <span v-else>No errors</span>
              </td>
              <td>
                <v-btn
                  variant="text"
                  size="small"
                  :to="`/client/${customer.id}`"
                  color="primary"
                >
                  View Details
                </v-btn>
              </td>
            </tr>
          </tbody>
        </v-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { api } from '@/services/api';
import type Client from '@/models/client';

const customers = ref<Client[]>([]);
const errorCounts = ref<Record<string, number>>({});
const loading = ref(false);

const getTokenStatusColor = (status: string) => {
  switch (status) {
    case 'Active':
      return 'success';
    case 'PendingAuthorization':
      return 'warning';
    case 'Expired':
    case 'Inactive':
    case 'Disabled':
    case 'Invalid':
    case 'Revoked':
      return 'error';
    default:
      return 'grey';
  }
};

const fetchCustomerErrors = async (customerId: string) => {
  try {
    const customerData = await api.getClient(customerId);
    if (customerData.errors) {
      errorCounts.value[customerId] = customerData.errors.length;
    }
  } catch (error) {
    console.error(`Error fetching errors for customer ${customerId}:`, error);
    errorCounts.value[customerId] = 0;
  }
};

const refreshData = async () => {
  loading.value = true;
  try {
    customers.value = await api.getClients();
    errorCounts.value = {};
    
    // Fetch errors for each customer
    await Promise.all(
      customers.value.map(customer => fetchCustomerErrors(customer.id))
    );
  } catch (error) {
    console.error('Error fetching customers:', error);
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  refreshData();
});
</script>
